import React, { useMemo } from 'react';
import classNames from 'classnames';
import { type TypographyVariant, Typography } from '@mui/material';

import { BRAZE_CONTENT_CARDS } from 'common/constants';
import { SL_WEB_PAYWALLS_V2 } from 'common/treatments';
import ForecastGraphsCTADaySelector from 'components/ForecastGraphsCTADaySelector';
import { CTACheckmarkIcon } from 'components/Icons/CTACheckmarkIcon';
import PremiumButtonCTA from 'components/PremiumButtonCTA';
import PremiumImageOverlayCTA from 'components/PremiumImageOverlayCTA';
import PremiumUpgradePillCTA from 'components/PremiumUpgradePillCTA';
import PremiumUpgradePillCTAV2 from 'components/PremiumUpgradePillCTAV2';
import { useTreatments } from 'utils/treatments';

import midrollCTAStyles from './MidrollCTA.module.scss';
import premiumAnalysisCTAStyles from './PremiumAnalysisCTA.module.scss';
import premiumChartCTAStyles from './PremiumChartCTA.module.scss';
import premiumCamLabelStyles from './PremiumCamLabel.module.scss';
import premiumTableCTAStyles from './PremiumTableCTA.module.scss';
import premiumSwellSpectraGraphCTAStyles from './PremiumSwellSpectraGraphCTAStyles.module.scss';
import premiumCustomForecastCTAStyles from './PremiumCustomForecastTooltipCTA.module.scss';
import premiumCustomForecastFavoritesCTAStyles from './PremiumCustomForecastFavoritesCTA.module.scss';
import premiumCustomForecastCTAImageStyles from './PremiumCustomForecastTooltipImageStyles.module.scss';
import premiumSwellSPectraCTAImageStyles from './PremiumSwellSpectraCTAImageStyles.module.scss';

export const GraphsCTADaySelector: React.FC<{
  segmentProperties?: {
    category: string;
    pageName: string;
    spotId: string;
    subregionId: string;
  };
}> = ({ segmentProperties }) => (
  <ForecastGraphsCTADaySelector segmentProperties={segmentProperties} />
);

export const PremiumSpotUpgradePill: React.FC<{
  segmentProperties: {
    location: string;
    subregionId: string;
    spotId: string;
    pageName: string;
  };
}> = ({ segmentProperties }) => {
  const treatments = useTreatments();
  return treatments[SL_WEB_PAYWALLS_V2] === 'on' ? (
    <PremiumUpgradePillCTAV2
      dataTest="premium-spot-upgrade-pill-cta"
      card={{ name: BRAZE_CONTENT_CARDS.PREMIUM_PILL_SPOT }}
      defaultCard={{
        extras: {
          box__title: 'Get more with Premium',
          box__body: 'Upgrade',
          box__link: '/upgrade',
        },
      }}
      segmentProperties={segmentProperties}
    />
  ) : (
    <PremiumUpgradePillCTA
      dataTest="premium-spot-upgrade-pill-cta"
      card={{ name: BRAZE_CONTENT_CARDS.PREMIUM_PILL_SPOT }}
      defaultCard={{
        extras: {
          box__title: 'Get more with Premium',
          box__body: 'Upgrade',
          box__link: '/upgrade',
        },
      }}
      segmentProperties={segmentProperties}
    />
  );
};

export const PremiumChartCTA: React.FC<{
  segmentProperties: {
    location: string;
    subregion: string;
    category: string;
    pageName: string;
  };
  className: string;
}> = ({ segmentProperties, className }) => (
  <PremiumButtonCTA
    className={className}
    cssModules={premiumChartCTAStyles}
    segmentProperties={segmentProperties}
    title="High-definition charts, at your finger tips"
    body="Get access to expert data with our propietary charts, and find your next wave, window and
    spot"
    buttonText="Upgrade"
    link="/upgrade"
  />
);

export const PremiumAnalysisCTA: React.FC<{
  segmentProperties: {
    location: string;
    contentId: string;
    subregionId: string;
    subregionName: string;
  };
  link?: string;
}> = ({ segmentProperties, link = '/upgrade' }) => (
  <PremiumButtonCTA
    cssModules={premiumAnalysisCTAStyles}
    segmentProperties={segmentProperties}
    title="Want access to Premium Analysis from the Surfline forecast team?"
    buttonText="Upgrade"
    link={link}
  />
);

export const PremiumTableCTA: React.FC<{
  segmentProperties: {
    location: string;
    category: string;
    pageName: string;
    spotId: string;
    subregionId: string;
  };
  className: string;
}> = ({ segmentProperties, className }) => (
  <PremiumButtonCTA
    className={className}
    cssModules={premiumTableCTAStyles}
    segmentProperties={segmentProperties}
    title="Swell is on the horizon."
    body="Get access to expert data with our propietary charts, and find your next wave, window and spot"
    buttonText="Upgrade"
    link="/upgrade"
  />
);

export interface BodyCopyBullets {
  bulletOne: string;
  bulletTwo: string;
  bulletThree: string;
}

export const PremiumSwellSpectraBody = ({ bullets }: { bullets: BodyCopyBullets }) => (
  <ul className={premiumSwellSpectraGraphCTAStyles.ctaBullets}>
    <li className={premiumSwellSpectraGraphCTAStyles.ctaBulletPoint}>
      <CTACheckmarkIcon className={premiumSwellSpectraGraphCTAStyles.ctaCheckmark} />
      <Typography component="p" variant="body1">
        {bullets.bulletOne}
      </Typography>
    </li>
    <li className={premiumSwellSpectraGraphCTAStyles.ctaBulletPoint}>
      <CTACheckmarkIcon className={premiumSwellSpectraGraphCTAStyles.ctaCheckmark} />
      <Typography component="p" variant="body1">
        {bullets.bulletTwo}
      </Typography>
    </li>
    <li className={premiumSwellSpectraGraphCTAStyles.ctaBulletPoint}>
      <CTACheckmarkIcon className={premiumSwellSpectraGraphCTAStyles.ctaCheckmark} />
      <Typography component="p" variant="body1">
        {bullets.bulletThree}
      </Typography>
    </li>
  </ul>
);

export const PremiumSwellSpectraGraphCTA: React.FC<{
  title: string;
  bodyCopyBullets: BodyCopyBullets;
  LeftContent?: () => React.ReactNode;
  segmentProperties: {
    location: string;
    category: string;
    pageName: string;
    spotId?: string;
    subregionId?: string;
    buoyId?: string;
    buoyName?: string;
  };
  native?: boolean;
}> = ({ bodyCopyBullets, LeftContent, segmentProperties, title, native }) => (
  <PremiumButtonCTA
    LeftContent={LeftContent}
    body={<PremiumSwellSpectraBody bullets={bodyCopyBullets} />}
    bodyVariant="body1"
    buttonText="Upgrade"
    buttonVariant="medium"
    cssModules={premiumSwellSpectraGraphCTAStyles}
    dataTest="graph-swell-spectra-cta"
    link={native ? 'surfline://upgrade' : '/upgrade'}
    segmentProperties={segmentProperties}
    title={title}
    titleVariant={'title3' as TypographyVariant}
  />
);

export const GraphSwellSpectraCTAImage = () => (
  <div className={premiumSwellSPectraCTAImageStyles.ctaImageWrapper}>
    <div className={premiumSwellSPectraCTAImageStyles.graphSwellSpectraCTAImage} />
  </div>
);

export const PremiumCamLabel: React.FC<{}> = () => (
  <div className={premiumCamLabelStyles.premiumCamLabel} data-testid="premium-cam-icon">
    <span className={premiumCamLabelStyles.premiumCamLabelInner}>Premium</span>
  </div>
);

const getMidrollCtaExtras = (upgradeUrl: string, isEntitled?: boolean, referrer?: string) =>
  isEntitled
    ? {
        box__h1: 'Watch waves, not ads.',
        box__h2: 'Upgrade your membership for ad-free cams.',
        button1__text: 'Upgrade now',
        button1__url: `/change-plan?referrerUrl=${referrer}`,
      }
    : {
        box__h1: 'Watch waves, not ads.',
        box__h2: 'Start your free trial now.',
        button1__text: 'Upgrade',
        button1__url: upgradeUrl,
      };

export const MidrollCTA: React.FC<{
  segmentProperties: {
    location: string;
    pageName: string;
    spotId: string;
    spotName: string;
  };
  cssModules?: {
    ctaContainer?: string;
    ctaGridContainer?: string;
    ctaImageWrapper?: string;
    ctaContent?: string;
    ctaLinks?: string;
    ctaHeading?: string;
    ctaSubheading?: string;
    ctaUpgradeBtn?: string;
    ctaAboutBtn?: string;
  };
  isEntitled: boolean;
  referrer?: string;
  stillUrl: string;
  spotName: string;
  upgradeUrl: string;
}> = ({ segmentProperties, cssModules, isEntitled, referrer, stillUrl, spotName, upgradeUrl }) => {
  const midrollCard = useMemo(
    () => ({
      name: 'CAM_MIDROLL_CTA',
      ...getMidrollCtaExtras(upgradeUrl, isEntitled, referrer),
    }),
    [isEntitled, referrer, upgradeUrl],
  );

  return (
    <div className={classNames(midrollCTAStyles.camMidrollUpsell, 'sl-cam-midroll-upsell')}>
      <PremiumImageOverlayCTA
        card={midrollCard}
        cssModules={cssModules}
        imageAlt="Recent conditions"
        imageUrl={stillUrl}
        segmentProperties={segmentProperties}
        spotName={spotName}
      />
    </div>
  );
};

export const PremiumCustomForecastTooltipCTA: React.FC<{
  dataTest?: string;
  enableAnimation: boolean;
  LeftContent?: () => React.ReactNode;
  segmentProperties: {
    location: string;
    category: string;
    pageName: string;
  };
  SecondaryButton?: () => React.ReactNode | null;
  showPaywall: boolean;
}> = ({
  dataTest,
  enableAnimation,
  LeftContent,
  segmentProperties,
  showPaywall,
  SecondaryButton,
}) => {
  const animateOnOpen = !enableAnimation || (enableAnimation && showPaywall);
  const premiumButtonCTAStyles = useMemo(
    () => ({
      ...premiumCustomForecastCTAStyles,
      ctaButton: animateOnOpen
        ? premiumCustomForecastCTAStyles.ctaButtonShow
        : premiumCustomForecastCTAStyles.ctaButtonHide,
      ctaContent: animateOnOpen
        ? premiumCustomForecastCTAStyles.ctaContentShow
        : premiumCustomForecastCTAStyles.ctaContentHide,
      ctaWrap: animateOnOpen
        ? premiumCustomForecastCTAStyles.ctaWrapShow
        : premiumCustomForecastCTAStyles.ctaWrapHide,
    }),
    [animateOnOpen],
  );
  return (
    <PremiumButtonCTA
      body="Premium members can create and save detailed 16-day forecasts for custom spots anywhere in the world."
      bodyVariant="body1"
      buttonText="Upgrade"
      buttonVariant="medium"
      cssModules={premiumButtonCTAStyles}
      dataTest={dataTest || 'custom-forecast-paywall'}
      LeftContent={LeftContent}
      link="/upgrade"
      SecondaryButton={SecondaryButton}
      segmentProperties={segmentProperties}
      title="Get swell forecasts for any point in the ocean."
      titleVariant={'title3' as TypographyVariant}
    />
  );
};

export const PremiumCustomForecastTooltipCTAImage = () => (
  <div className={premiumCustomForecastCTAImageStyles.ctaImageWrapper}>
    <div className={premiumCustomForecastCTAImageStyles.customForecastTooltipCTAImage} />
  </div>
);

export const PremiumCustomForecastFavoritesCTA: React.FC<{
  dataTest?: string;
  segmentProperties: {
    location: string;
    category: string;
    pageName: string;
  };
}> = ({ dataTest, segmentProperties }) => (
  <PremiumButtonCTA
    body="Premium members can create and save detailed 16-day forecasts for custom spots anywhere in the world."
    bodyVariant="body1"
    buttonText="Upgrade"
    buttonVariant="medium"
    cssModules={{
      ctaTitle: premiumCustomForecastFavoritesCTAStyles.ctaTitle,
      ctaBody: premiumCustomForecastFavoritesCTAStyles.ctaBody,
      ctaButton: premiumCustomForecastFavoritesCTAStyles.ctaButton,
    }}
    dataTest={dataTest || 'custom-forecast-favorites-paywall'}
    link="/upgrade"
    segmentProperties={segmentProperties}
    title="Get swell forecasts for any point in the ocean."
    titleVariant={'title3' as TypographyVariant}
  />
);
